// Disposable adapter for the presentation-layer preview (docs/plans/2026-08-26-presentation-layer.md).
// Builds a real ConfigVersion from the checklist-approved dummy content fixture, and stands in for
// UC-2c (LLM ranking/reasoning, not yet built) with canned per-candidate copy. Every other file in
// src/presentation/ is written against real types and functions; this is the one file that gets
// deleted and replaced once the Session API and UC-2c ranking exist. See story-decomposition.md,
// "Present the flow as a non-form experience" > "Client-side preview harness, against mock data".

import { transformSheetTabs } from "../config/transform";
import { dummyContentSheetTabs } from "../../test/fixtures/dummy-content";
import type { ConfigVersion, Solution } from "../types";

const VERSION_ID = "preview-mock-v1";

export function loadMockConfig(): ConfigVersion {
  const transformed = transformSheetTabs(dummyContentSheetTabs);
  return {
    ...transformed,
    versionId: VERSION_ID,
    publishedAt: new Date().toISOString(),
    sourceChecksum: "mock",
    // Preview-only, matched when the harness's "Page path" box is set to /start.
    campaigns: [
      {
        id: "preview-start",
        urlPath: "/start",
        description: "Thank you for trying out the new discovery experience, use coupon code PREVIEW30 for 30% off",
        code: "PREVIEW30",
        expires: "2099-12-31",
      },
    ],
  };
}

/**
 * Stand-in for UC-2c's structured model output (member_id, confidence, reasoning, reflectedProblem
 * ordered first). Real ranking reads the visitor's verbatim answers and LuAnn's voice reference;
 * this is a canned line per candidate so the result screen has something plausible to reveal while
 * that work is unbuilt. Never used for anything but this preview.
 */
export interface MockRecommendation {
  reflectedProblem: string;
  reasoning: string;
  confidence: number;
}

const REASONING_BY_SOLUTION: Record<string, MockRecommendation> = {
  dana: {
    reflectedProblem: "It sounds like a lot of what keeps your business running only lives in your head right now.",
    reasoning: "Dana specializes in turning what's stuck in an owner's head into a system the team can actually run, which is exactly the gap showing up in your answers.",
    confidence: 0.82,
  },
  marcus: {
    reflectedProblem: "It sounds like your team keeps coming back to you for decisions they could be making themselves.",
    reasoning: "Marcus works specifically on handing off real authority, not just tasks, which lines up with what's holding your team back.",
    confidence: 0.79,
  },
  priya: {
    reflectedProblem: "It sounds like the plan is usually clear, but follow-through is where things slip.",
    reasoning: "Priya builds accountability into a team's day-to-day so commitments actually get kept without you having to police it.",
    confidence: 0.76,
  },
  jordan: {
    reflectedProblem: "It sounds like projects are quietly losing time and margin to estimating and workflow gaps.",
    reasoning: "Jordan fixes exactly this: the estimating and process gaps that eat margin before anyone notices.",
    confidence: 0.8,
  },
  renee: {
    reflectedProblem: "It sounds like the leads coming in aren't the ones you actually want.",
    reasoning: "Renee focuses on getting your marketing to attract the right-fit clients, not just more of them.",
    confidence: 0.78,
  },
  ilya: {
    reflectedProblem: "It sounds like not enough of the right people are finding you in the first place.",
    reasoning: "Ilya builds visibility that leads to real inquiries, which is the piece missing here.",
    confidence: 0.74,
  },
  sofia: {
    reflectedProblem: "It sounds like good conversations aren't consistently turning into booked clients.",
    reasoning: "Sofia specializes in the conversion moment itself, including the pricing conversation that often derails it.",
    confidence: 0.81,
  },
  ben: {
    reflectedProblem: "It sounds like you're not fully confident which of your projects are actually making money.",
    reasoning: "Ben helps owners see which work is profitable and price the rest with confidence.",
    confidence: 0.77,
  },
};

const FALLBACK: MockRecommendation = {
  reflectedProblem: "It sounds like there's a real opportunity to get you unstuck here.",
  reasoning: "This Board Member's specialties line up well with what you described.",
  confidence: 0.6,
};

export function mockRecommendationFor(solution: Solution): MockRecommendation {
  return REASONING_BY_SOLUTION[solution.id] ?? FALLBACK;
}

/** Stand-in for an alternate's model-written alternateReason, so the preview shows the same card
 * shape the live result screen does. Same disposable status as everything else in this file. */
export function mockAlternateReasonFor(alternate: Solution, primary: Solution): string {
  return `${alternate.name} works on part of what you described, from a different angle. ${primary.name} is the closer fit for what you put first, which is why they lead.`;
}
